import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Collapse, Pagination } from 'react-bootstrap';
import templateService from '../../../services/template.service';
import Loading from '../../common/Loading';
const MEDIUM_BADGE = {
    EMAIL: 'admin-templates-medium-badge admin-templates-medium-badge--email',
    SMS: 'admin-templates-medium-badge admin-templates-medium-badge--sms',
    CALL: 'admin-templates-medium-badge admin-templates-medium-badge--call',
};

const USED_BY_BADGE = {
    CLINIC: 'admin-templates-usedby-badge admin-templates-usedby-badge--clinic',
    ECX: 'admin-templates-usedby-badge admin-templates-usedby-badge--ecx',
};

const formatUsedByLabel = (usedBy) => {
    if (usedBy === 'CLINIC') return 'Clinic';
    if (usedBy === 'ECX') return 'ECX';
    return usedBy || '—';
};

function UsedByBadge({ usedBy }) {
    const usedByKey = usedBy?.toUpperCase();
    return (
        <span className={USED_BY_BADGE[usedByKey] || 'admin-templates-usedby-badge'}>
            {formatUsedByLabel(usedByKey)}
        </span>
    );
}

function AdminTemplateList({ searchPanelOpen = false, onSearchPanelToggle, onCreateTemplate }) {
    const navigate = useNavigate();
    const [templateList, setTemplateList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);
    const [mediumTypeFilter, setMediumTypeFilter] = useState('');
    const [pagination, setPagination] = useState({
        page: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
    });

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const getTemplates = async () => {
            try {
                setIsLoading(true);
                setError(null);
                setExpandedRows([]);

                const res = await templateService.getEcxTemplate(
                    pagination.page,
                    pagination.size,
                    mediumTypeFilter || null,
                    { signal: controller.signal }
                );

                if (isMounted) {
                    setTemplateList(res.data.content || []);
                    setPagination((prev) => ({
                        ...prev,
                        totalElements: res.data.totalElements || 0,
                        totalPages: res.data.totalPages || 0,
                    }));

                    if ((res.data.content || []).length === 0 && mediumTypeFilter) {
                        setError('No templates found matching your filters.');
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError' && isMounted) {
                    console.error('Fetch error:', err);
                    setError('Failed to fetch templates. Please try again.');
                    setTemplateList([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        getTemplates();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [pagination.page, pagination.size, mediumTypeFilter]);

    const handleClearFilters = () => {
        setMediumTypeFilter('');
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const toggleRow = (id) => {
        setExpandedRows((prev) =>
            prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
        );
    };

    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    };

    const handleSizeChange = (event) => {
        setPagination((prev) => ({
            ...prev,
            size: Number(event.target.value),
            page: 0,
        }));
    };

    const handleEditClick = (template) => {
        const { id, mediumType } = template;

        switch (mediumType) {
            case 'SMS':
                navigate(`edit-sms/${id}`);
                break;
            case 'EMAIL':
                navigate(`edit-email/${id}`);
                break;
            case 'CALL':
                navigate(`edit-call/${id}`);
                break;
            default:
                navigate(`edit/${id}`);
        }
    };

    const getPaginationItems = () => {
        const items = [];
        const currentPage = pagination.page;
        const totalPages = pagination.totalPages;
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item
                key={1}
                active={currentPage === 0}
                onClick={() => handlePageChange(0)}
                disabled={isLoading}
            >
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => handlePageChange(number - 1)}
                    disabled={isLoading}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => handlePageChange(totalPages - 1)}
                    disabled={isLoading}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const startItem = templateList.length > 0 ? pagination.page * pagination.size + 1 : 0;
    const endItem = Math.min((pagination.page + 1) * pagination.size, pagination.totalElements);

    const paginationControls =
        pagination.totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First
                    onClick={() => handlePageChange(0)}
                    disabled={pagination.page === 0 || isLoading}
                />
                <Pagination.Prev
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 0 || isLoading}
                />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages - 1 || isLoading}
                />
                <Pagination.Last
                    onClick={() => handlePageChange(pagination.totalPages - 1)}
                    disabled={pagination.page >= pagination.totalPages - 1 || isLoading}
                />
            </Pagination>
        ) : null;

    return (
        <>
            <div className="d-flex align-items-end justify-content-between gap-2 mb-3">
                <div className="mb-0 d-flex align-items-center flex-wrap gap-1">
                    <i className="bi bi-journal-text text-primary me-1"></i>
                    <h5 className="card-title mb-0 admin-templates-card__title">
                        Template Library
                        <span className="admin-templates-card__count">
                            ({pagination.totalElements} template{pagination.totalElements !== 1 ? 's' : ''}{' '}
                            {mediumTypeFilter ? 'matching filter' : 'total'})
                        </span>
                    </h5>
                </div>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm flex-shrink-0"
                    onClick={onSearchPanelToggle}
                    aria-expanded={searchPanelOpen}
                    title={searchPanelOpen ? 'Hide filters' : 'Show filters'}
                >
                    <i className="bi bi-funnel me-1"></i>
                    Filter
                </button>
            </div>

            <Collapse in={searchPanelOpen}>
                <div>
                    <div className="process-queue-filters mb-4 p-3 bg-light rounded border">
                        <div className="row g-2 g-md-3 align-items-center process-queue-filters__row">
                            <div className="col-6 col-md-3 col-lg-auto process-queue-filters__item">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text process-queue-filters__label">Type</span>
                                    <select
                                        className="form-select form-select-sm process-queue-filters__control"
                                        value={mediumTypeFilter}
                                        onChange={(e) => {
                                            setMediumTypeFilter(e.target.value);
                                            setPagination((prev) => ({ ...prev, page: 0 }));
                                        }}
                                    >
                                        <option value="">All types</option>
                                        <option value="EMAIL">Email</option>
                                        <option value="SMS">SMS</option>
                                        <option value="CALL">Call</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-12 col-lg-auto process-queue-filters__actions d-flex flex-nowrap gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleClearFilters}
                                    disabled={!mediumTypeFilter}
                                    title="Clear filters"
                                >
                                    <i className="bi bi-x-lg me-1"></i>
                                    Clear
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Collapse>

            {isLoading ? (
                <Loading />
            ) : error && templateList.length === 0 ? (
                <Alert variant="warning" className="border-0 shadow-sm">
                    {error}
                </Alert>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table admin-templates-table">
                        <thead>
                            <tr>
                                <th>
                                    <i className="bi bi-bookmark"></i> Template Name
                                </th>
                                <th>
                                    <i className="bi bi-layers"></i> Medium Type
                                </th>
                                <th>
                                    <i className="bi bi-chat-left-text"></i> Content
                                </th>
                                <th>
                                    <i className="bi bi-building"></i> Used By
                                </th>
                                <th>
                                    <i className="bi bi-calendar-event"></i> Created At
                                </th>
                                <th className="text-end">
                                    <i className="bi bi-gear"></i> Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {templateList.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted py-5">
                                        No templates found.
                                        {onCreateTemplate && (
                                            <>
                                                {' '}
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 align-baseline"
                                                    onClick={() => onCreateTemplate('EMAIL')}
                                                >
                                                    Create one
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                templateList.map((template) => (
                                    <React.Fragment key={template.id}>
                                        <tr>
                                            <td>
                                                <div className="fw-semibold">
                                                    {template.reminderName || 'Unnamed Template'}
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    className={
                                                        MEDIUM_BADGE[template.mediumType] ||
                                                        'admin-templates-medium-badge'
                                                    }
                                                >
                                                    {template.mediumType || '—'}
                                                </span>
                                            </td>
                                            <td>
                                                <div
                                                    className="admin-templates-content-preview"
                                                    title={template.content}
                                                >
                                                    {template.content || '—'}
                                                </div>
                                            </td>
                                            <td>
                                                <UsedByBadge usedBy={template.usedBy} />
                                            </td>
                                            <td>
                                                <small className="text-muted">{template.createdAt || '—'}</small>
                                            </td>
                                            <td className="text-end">
                                                <div className="d-inline-flex gap-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleEditClick(template)}
                                                        title="Edit template"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </Button>
                                                    <Button
                                                        variant={
                                                            expandedRows.includes(template.id)
                                                                ? 'outline-secondary'
                                                                : 'outline-primary'
                                                        }
                                                        size="sm"
                                                        onClick={() => toggleRow(template.id)}
                                                        title={
                                                            expandedRows.includes(template.id)
                                                                ? 'Collapse details'
                                                                : 'Expand details'
                                                        }
                                                        aria-expanded={expandedRows.includes(template.id)}
                                                    >
                                                        <i
                                                            className={`bi bi-chevron-${
                                                                expandedRows.includes(template.id) ? 'up' : 'down'
                                                            }`}
                                                        ></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={6} className="p-0 border-top-0">
                                                <Collapse in={expandedRows.includes(template.id)}>
                                                    <div className="admin-templates-detail-panel">
                                                        <div className="row g-3">
                                                            <div className="col-md-6">
                                                                <h6 className="small fw-semibold text-uppercase text-muted mb-2">
                                                                    Template Details
                                                                </h6>
                                                                <div className="small mb-2">
                                                                    <strong>ID:</strong>{' '}
                                                                    <span className="font-monospace text-muted">
                                                                        {template.id}
                                                                    </span>
                                                                </div>
                                                                <div className="small mb-2">
                                                                    <strong>Reminder Type ID:</strong>{' '}
                                                                    <span className="font-monospace text-muted">
                                                                        {template.reminderTypeId || '—'}
                                                                    </span>
                                                                </div>
                                                                <div className="small">
                                                                    <strong>Full Content:</strong>
                                                                    <div className="admin-templates-detail-content mt-1">
                                                                        {template.content || '—'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <h6 className="small fw-semibold text-uppercase text-muted mb-2">
                                                                    Timestamps
                                                                </h6>
                                                                <div className="small mb-2">
                                                                    <strong>Created:</strong>{' '}
                                                                    <span className="text-muted">
                                                                        {template.createdAt || '—'}
                                                                    </span>
                                                                </div>
                                                                <div className="small mb-2">
                                                                    <strong>Modified:</strong>{' '}
                                                                    <span className="text-muted">
                                                                        {template.modifiedAt || '—'}
                                                                    </span>
                                                                </div>
                                                                {template.modifiedAt &&
                                                                    template.modifiedAt !== template.createdAt && (
                                                                        <Alert
                                                                            variant="light"
                                                                            className="border py-2 px-3 small mb-0"
                                                                        >
                                                                            <i className="bi bi-info-circle me-1"></i>
                                                                            This template has been modified since
                                                                            creation.
                                                                        </Alert>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted">Show</span>
                        <select
                            className="form-select form-select-sm admin-templates-page-size"
                            value={pagination.size}
                            onChange={handleSizeChange}
                            disabled={isLoading}
                        >
                            {[10, 20, 50, 100].map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        <span className="small text-muted">entries</span>
                    </div>
                    <span className="text-muted small">
                        Showing {startItem} to {endItem} of {pagination.totalElements}
                    </span>
                </div>
                {paginationControls}
            </div>
        </>
    );
}

export default AdminTemplateList;
